import { useState, useEffect, useCallback, useRef } from 'react';
import { tunnelsAPI, Tunnel } from '../api/tunnels';

export interface UseTunnelsOptions {
  autoFetch?: boolean;
  refreshInterval?: number | null;
}

export interface ExtendedTunnel extends Tunnel {
  /** Адрес подключения — всегда host:port */
  publicAddress?: string;
  /** Привязанный домен, если есть */
  customDomain?: string;
  /** Домен подтверждён (NS делегированы на нас) */
  domainVerified?: boolean;
  /** Включён ли SSL/HTTPS */
  sslEnabled?: boolean;
  status?: string;
}

export interface UseTunnelsResult {
  tunnels: ExtendedTunnel[];
  loading: boolean;
  error: string | null;
  fetchTunnels: (options?: { silent?: boolean; statusOnly?: boolean }) => Promise<ExtendedTunnel[]>;
  createTunnel: (tunnelData: unknown) => Promise<ExtendedTunnel>;
  updateTunnel: (id: string, data: unknown) => Promise<ExtendedTunnel>;
  deleteTunnel: (id: string) => Promise<boolean>;
  refresh: () => Promise<ExtendedTunnel[]>;
  activeTunnels: ExtendedTunnel[];
  inactiveTunnels: ExtendedTunnel[];
}

const stableStringify = (value: unknown): string => {
  if (value === null || value === undefined) {
    return String(value);
  }

  if (typeof value !== 'object') {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map(item => stableStringify(item)).join(',')}]`;
  }

  const sortedObject = Object.entries(value as Record<string, unknown>)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, val]) => `"${key}":${stableStringify(val)}`)
    .join(',');

  return `{${sortedObject}}`;
};

const normalizeTunnel = (tunnel: Tunnel): ExtendedTunnel => {
  const host = typeof tunnel.public_host === 'string' ? tunnel.public_host : undefined;
  const port = typeof tunnel.public_port === 'number' ? tunnel.public_port : undefined;

  const domain = typeof tunnel.custom_domain === 'string' ? tunnel.custom_domain.trim() : '';

  return {
    ...tunnel,
    id: tunnel.tunnel_id || tunnel.id,
    customDomain: domain || undefined,
    domainVerified: Boolean(tunnel.domain_verified),
    sslEnabled: Boolean(tunnel.ssl_enabled),
    // Основной адрес туннеля не меняется — всегда host:port.
    // Домен показывается отдельно, чтобы не подменять адрес подключения.
    publicAddress: host && port ? `${host}:${port}` : undefined,
  };
};

const isTunnelEqual = (left: ExtendedTunnel, right: ExtendedTunnel): boolean => {
  return stableStringify(left) === stableStringify(right);
};

const reconcileTunnels = (
  prevTunnels: ExtendedTunnel[],
  nextTunnels: ExtendedTunnel[]
): ExtendedTunnel[] => {
  if (prevTunnels.length === 0 && nextTunnels.length === 0) {
    return prevTunnels;
  }

  const prevById = new Map<string, ExtendedTunnel>();
  prevTunnels.forEach((tunnel, index) => {
    const id = String(tunnel.id ?? `idx:${index}`);
    prevById.set(id, tunnel);
  });

  let hasChanged = prevTunnels.length !== nextTunnels.length;

  const merged = nextTunnels.map((nextTunnel, index) => {
    const id = String(nextTunnel.id ?? `idx:${index}`);
    const prevTunnel = prevById.get(id);

    if (!prevTunnel) {
      hasChanged = true;
      return nextTunnel;
    }

    if (isTunnelEqual(prevTunnel, nextTunnel)) {
      return prevTunnel;
    }

    hasChanged = true;
    return nextTunnel;
  });

  if (!hasChanged && prevTunnels.every((tunnel, index) => tunnel === merged[index])) {
    return prevTunnels;
  }

  return merged;
};

const reconcileTunnelsByStatus = (
  prevTunnels: ExtendedTunnel[],
  nextTunnels: ExtendedTunnel[]
): ExtendedTunnel[] => {
  if (prevTunnels.length === 0 && nextTunnels.length === 0) {
    return prevTunnels;
  }

  const prevById = new Map<string, ExtendedTunnel>();
  prevTunnels.forEach((tunnel, index) => {
    const id = String(tunnel.id ?? `idx:${index}`);
    prevById.set(id, tunnel);
  });

  let hasChanged = prevTunnels.length !== nextTunnels.length;

  const merged = nextTunnels.map((nextTunnel, index) => {
    const id = String(nextTunnel.id ?? `idx:${index}`);
    const prevTunnel = prevById.get(id);

    if (!prevTunnel) {
      hasChanged = true;
      return nextTunnel;
    }

    if (prevTunnel.status === nextTunnel.status) {
      return prevTunnel;
    }

    hasChanged = true;
    return nextTunnel;
  });

  if (!hasChanged && prevTunnels.every((tunnel, index) => tunnel === merged[index])) {
    return prevTunnels;
  }

  return merged;
};

/**
 * Custom hook for managing tunnels
 * @param options - Hook options
 * @returns Tunnels state and methods
 */
export const useTunnels = (options: UseTunnelsOptions = {}): UseTunnelsResult => {
  const { autoFetch = true, refreshInterval = null } = options;

  const [tunnels, setTunnels] = useState<ExtendedTunnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tunnelsRef = useRef<ExtendedTunnel[]>([]);

  useEffect(() => {
    tunnelsRef.current = tunnels;
  }, [tunnels]);

  /**
   * Fetch all tunnels
   */
  const fetchTunnels = useCallback(async (options?: { silent?: boolean; statusOnly?: boolean }): Promise<ExtendedTunnel[]> => {
    const silent = options?.silent ?? false;
    const statusOnly = options?.statusOnly ?? false;

    if (!silent) {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await tunnelsAPI.getAll();
      const normalizedTunnels: ExtendedTunnel[] = (data || []).map(normalizeTunnel);

      let resolvedTunnels = normalizedTunnels;
      setTunnels(prevTunnels => {
        const reconciled = statusOnly
          ? reconcileTunnelsByStatus(prevTunnels, normalizedTunnels)
          : reconcileTunnels(prevTunnels, normalizedTunnels);
        resolvedTunnels = reconciled;
        return reconciled;
      });

      return resolvedTunnels;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      throw err;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  /**
   * Create new tunnel
   */
  const createTunnel = useCallback(
    async (tunnelData: unknown): Promise<ExtendedTunnel> => {
      setLoading(true);
      setError(null);
      try {
        const newTunnel = await tunnelsAPI.create(tunnelData);
        const tunnelWithAddress: ExtendedTunnel = normalizeTunnel(newTunnel);
        setTunnels(prev => [...prev, tunnelWithAddress]);
        return tunnelWithAddress;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Update tunnel
   */
  const updateTunnel = useCallback(
    async (id: string, data: unknown): Promise<ExtendedTunnel> => {
      setLoading(true);
      setError(null);
      try {
        const updatedTunnel = await tunnelsAPI.update(id, data);
        const tunnelWithAddress: ExtendedTunnel = normalizeTunnel(updatedTunnel);
        setTunnels(prev => prev.map(t => (t.id === id ? tunnelWithAddress : t)));
        return tunnelWithAddress;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Delete tunnel
   */
  const deleteTunnel = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      setError(null);
      try {
        await tunnelsAPI.delete(id);
        setTunnels(prev => prev.filter(t => t.id !== id));
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(message);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Refresh tunnels
   */
  const refresh = useCallback(() => {
    return fetchTunnels();
  }, [fetchTunnels]);

  // Initial fetch
  useEffect(() => {
    if (autoFetch) {
      void fetchTunnels();
    }
  }, [autoFetch, fetchTunnels]);

  // Auto-refresh (адаптивный: при пустом списке реже)
  useEffect(() => {
    if (!refreshInterval || !autoFetch) return;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const baseInterval = Math.max(refreshInterval, 1000);
    const emptyInterval = Math.max(baseInterval * 3, 15000);

    const schedule = (delay: number): void => {
      timeoutId = setTimeout(() => {
        void runTick();
      }, delay);
    };

    const runTick = async (): Promise<void> => {
      if (cancelled) return;

      try {
        const latest = await fetchTunnels({ silent: true, statusOnly: true });
        if (!cancelled) {
          const nextDelay = latest.length === 0 ? emptyInterval : baseInterval;
          schedule(nextDelay);
        }
      } catch (_err) {
        if (!cancelled) {
          const fallbackDelay = tunnelsRef.current.length === 0 ? emptyInterval : baseInterval;
          schedule(fallbackDelay);
        }
      }
    };

    const initialDelay = tunnelsRef.current.length === 0 ? emptyInterval : baseInterval;
    schedule(initialDelay);

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshInterval, autoFetch, fetchTunnels]);

  return {
    // State
    tunnels,
    loading,
    error,
    // Actions
    fetchTunnels,
    createTunnel,
    updateTunnel,
    deleteTunnel,
    refresh,
    // Derived state
    activeTunnels: tunnels.filter(t => t.status === 'active'),
    inactiveTunnels: tunnels.filter(t => t.status !== 'active'),
  };
};
